// Trustpilot "Explicit Invitations" integration.
//
// This is separate from the public TrustBox widget (TrustpilotReviews.js). The
// invitation script (invitejs.trustpilot.com/tp.min.js) exposes a global `tp()`
// command queue. After registering the BusinessUnit we can trigger
// `tp('createInvitation', {...})` to invite a specific customer to leave a
// review. We only ever fire this when the user explicitly provides an email.
//
// GDPR / consent note: no invitation is ever sent automatically. The email is
// entered voluntarily by the user in the export flow, so this is opt-in.

const BUSINESS_UNIT_ID =
  process.env.REACT_APP_TRUSTPILOT_BUSINESS_UNIT_ID || '6aac2c27177fe58c3a845ad7';

let invitePromise = null;

// Loads the Trustpilot invitation JS once and registers the BusinessUnit.
function loadInviteScript() {
  if (typeof window === 'undefined') return Promise.resolve(null);
  if (window.tp && window.__tpRegistered) return Promise.resolve(window.tp);
  if (invitePromise) return invitePromise;

  invitePromise = new Promise((resolve) => {
    try {
      // Standard Trustpilot invitation bootstrap (command-queue pattern).
      (function (w, d, s, r, n) {
        w.TrustpilotObject = n;
        w[n] = w[n] || function () { (w[n].q = w[n].q || []).push(arguments); };
        const a = d.createElement(s);
        a.async = 1;
        a.src = r;
        a.type = 'text/java' + s;
        const f = d.getElementsByTagName(s)[0];
        f.parentNode.insertBefore(a, f);
      })(window, document, 'script', 'https://invitejs.trustpilot.com/tp.min.js', 'tp');

      window.tp('register', BUSINESS_UNIT_ID);
      window.__tpRegistered = true;
      resolve(window.tp);
    } catch (e) {
      resolve(null);
    }
  });
  return invitePromise;
}

// Very small email sanity check — the field is optional so we stay lenient.
export function isValidEmail(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(email || '').trim());
}

/**
 * Triggers a Trustpilot review invitation for the given recipient.
 * No-ops (and returns false) if the email is missing/invalid.
 *
 * @param {{email:string, name?:string, referenceId?:string}} opts
 * @returns {Promise<boolean>} whether an invitation command was queued
 */
export async function sendTrustpilotInvitation({ email, name = '', referenceId = '' } = {}) {
  if (!isValidEmail(email)) return false;
  try {
    const tp = await loadInviteScript();
    if (!tp) return false;
    tp('createInvitation', {
      recipientEmail: String(email).trim(),
      recipientName: (name || String(email).split('@')[0]).trim(),
      referenceId: referenceId || `bc-${Date.now()}`,
      source: 'InvitationScript',
    });
    return true;
  } catch (e) {
    return false;
  }
}
